import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  Table,
  TableHeader,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Button,
} from "@capacit-com/lara-law-shared-ui";
import SubscribeButton from "@/components/buttons/SubscribeButton";
import {
  Subscription,
  SubscriptionCategory,
} from "@/models/subscriptions/Subscription";
import useUsers from "@/hooks/useUsers";
import {
  parseLegislationEntityPath,
  parseWorkspaceDocumentEntityPath,
  parseWorkspaceEntityPath,
} from "@/utils/entityPathParser";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import {
  legislationTarget,
  NEW_TAB_MODIFIER_TOOLTIP,
  workspaceDocumentTarget,
} from "@/lib/navigation/appNavigation";
import { SubscriptionsWidgetConfig } from "@/models/dashboard/WidgetConfig";
import { Bell, ChevronLeft, ChevronRight, GripVertical, RefreshCw } from "lucide-react";
import {
  buildFullDisplayOrderForGlobalUpdate,
  resolveItemOrder,
  usesWidgetItemOrder,
} from "../lib/resolveItemOrder";
import { useItemService } from "@/hooks/useServices";

const SUBSCRIPTIONS_PAGE_SIZE = 20;

interface SubscriptionsWidgetProps {
  widgetId: string;
  name: string;
  config: SubscriptionsWidgetConfig;
  isEditMode: boolean;
  onConfigUpdate?: (config: SubscriptionsWidgetConfig) => void;
}

const resolveAllSubscriptionIds = (
  totalItems: number,
  subscriptions: Subscription[],
  displayOrder: string[],
  itemOrder: string[] | undefined,
  prefetchedIds: string[] | null
): string[] | null => {
  if (totalItems === 0) {
    return [];
  }

  const orderSource =
    itemOrder && itemOrder.length > 0 ? itemOrder : displayOrder;

  if (orderSource.length === totalItems) {
    return orderSource;
  }

  if (totalItems === subscriptions.length) {
    return subscriptions.map((sub) => sub.id);
  }

  if (prefetchedIds && prefetchedIds.length === totalItems) {
    return prefetchedIds;
  }

  return null;
};

const SubscriptionsWidget: FC<SubscriptionsWidgetProps> = ({
  widgetId: _widgetId,
  name: _name,
  config,
  isEditMode,
  onConfigUpdate,
}) => {
  const { useQueryDetailedSubscriptions } = useUsers();
  const userService = useItemService((services) => services.user);
  const { openNavigation } = useOpenAppNavigation();
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchPageSize, setFetchPageSize] = useState(SUBSCRIPTIONS_PAGE_SIZE);
  const allSubscriptionIdsRef = useRef<string[] | null>(null);

  const usesWidgetOrder = usesWidgetItemOrder(
    config.useGlobalOrder,
    config.itemOrder
  );

  const detailedSubscriptions = useQueryDetailedSubscriptions(
    usesWidgetOrder ? 1 : currentPage,
    usesWidgetOrder ? fetchPageSize : SUBSCRIPTIONS_PAGE_SIZE
  );

  const queryData = detailedSubscriptions.data;
  const isQueryReady = !!queryData;

  const displayOrder = useMemo(
    () => queryData?.displayOrder ?? [],
    [queryData?.displayOrder]
  );
  const totalItems =
    queryData?.totalRecords ?? queryData?.totalCount ?? 0;
  const serverPageData = useMemo(
    () => queryData?.data ?? [],
    [queryData?.data]
  );

  useEffect(() => {
    if (!usesWidgetOrder) {
      setFetchPageSize(SUBSCRIPTIONS_PAGE_SIZE);
      return;
    }

    if (!isQueryReady || totalItems <= fetchPageSize) {
      return;
    }

    setFetchPageSize(totalItems);
  }, [usesWidgetOrder, isQueryReady, totalItems, fetchPageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [usesWidgetOrder]);

  useEffect(() => {
    allSubscriptionIdsRef.current = null;
    if (totalItems === 0) {
      return;
    }

    const orderSource =
      config.itemOrder && config.itemOrder.length > 0
        ? config.itemOrder
        : displayOrder;

    if (orderSource.length === totalItems) {
      allSubscriptionIdsRef.current = orderSource;
      return;
    }

    if (usesWidgetOrder && serverPageData.length >= totalItems) {
      allSubscriptionIdsRef.current = serverPageData.map((sub) => sub.id);
      return;
    }

    if (totalItems === serverPageData.length) {
      allSubscriptionIdsRef.current = serverPageData.map((sub) => sub.id);
      return;
    }

    let cancelled = false;
    void userService
      .getDetailedSubscriptions(1, Math.max(totalItems, 1))
      .then((response) => {
        if (cancelled || !response.success) {
          return;
        }
        allSubscriptionIdsRef.current = response.value.data.map((sub) => sub.id);
      });

    return () => {
      cancelled = true;
    };
  }, [
    totalItems,
    displayOrder,
    config.itemOrder,
    serverPageData,
    usesWidgetOrder,
    userService,
  ]);

  const formatCategory = (category: string) => {
    switch (category) {
      case "Legislation":
        return "Kilder";
      case "Workspace":
        return "Workspace";
      case "WorkspaceDocument":
        return "Dokument";
      case "Conversation":
        return "Samtale";
      default:
        return category;
    }
  };

  const goToSubbedItem = (sub: Subscription, event?: React.MouseEvent) => {
    switch (sub.entityType) {
      case SubscriptionCategory.WorkspaceDocument: {
        const { workspaceId, documentId } = parseWorkspaceDocumentEntityPath(
          sub.entityPath
        );
        openNavigation(
          workspaceDocumentTarget(workspaceId, documentId),
          event
        );
        break;
      }
      case SubscriptionCategory.Legislation: {
        const { externalId, datasource } = parseLegislationEntityPath(
          sub.entityPath
        );
        openNavigation(legislationTarget(externalId, datasource), event);
        break;
      }
      case SubscriptionCategory.Workspace: {
        const { workspaceId: wId } = parseWorkspaceEntityPath(sub.entityPath);
        openNavigation({ kind: "workspaceFiles", workspaceId: wId }, event);
        break;
      }
      case SubscriptionCategory.Conversation:
      default:
        break;
    }
  };

  const orderedAll = useMemo(
    () =>
      resolveItemOrder(serverPageData, {
        globalOrder: displayOrder,
        itemOrder: config.itemOrder,
        useGlobalOrder: config.useGlobalOrder,
      }),
    [serverPageData, displayOrder, config.itemOrder, config.useGlobalOrder]
  );

  const orderedSubscriptions = useMemo(() => {
    if (!usesWidgetOrder) {
      return orderedAll;
    }

    const start = (currentPage - 1) * SUBSCRIPTIONS_PAGE_SIZE;
    return orderedAll.slice(start, start + SUBSCRIPTIONS_PAGE_SIZE);
  }, [usesWidgetOrder, orderedAll, currentPage]);

  const totalPages = usesWidgetOrder
    ? Math.max(1, Math.ceil(orderedAll.length / SUBSCRIPTIONS_PAGE_SIZE))
    : isQueryReady
      ? (detailedSubscriptions.data.totalPages ?? 1)
      : 1;

  const handleDragEnd = (result: DropResult) => {
    if (!isEditMode || !onConfigUpdate || !result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const reordered = [...orderedSubscriptions];
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destinationIndex, 0, removed);
    const visibleOrderedIds = reordered.map((sub) => sub.id);

    const allIds = resolveAllSubscriptionIds(
      totalItems,
      serverPageData,
      displayOrder,
      config.itemOrder,
      allSubscriptionIdsRef.current
    );
    if (!allIds) {
      return;
    }

    onConfigUpdate({
      ...config,
      useGlobalOrder: false,
      itemOrder: buildFullDisplayOrderForGlobalUpdate(
        visibleOrderedIds,
        allIds,
        config.itemOrder ?? displayOrder
      ),
    });
  };

  const isLoadingWidgetOrderList =
    usesWidgetOrder &&
    isQueryReady &&
    totalItems > 0 &&
    serverPageData.length < totalItems;

  if (detailedSubscriptions.isPending || isLoadingWidgetOrderList) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="h-5 w-5 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (
    orderedSubscriptions.length === 0 &&
    totalItems === 0 &&
    !detailedSubscriptions.isFetching
  ) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-3 text-muted-foreground">
        <Bell className="h-8 w-8" />
        <p className="text-sm">Ingen aktive abonnementer</p>
      </div>
    );
  }

  const pageStart =
    totalItems === 0
      ? 0
      : (currentPage - 1) * SUBSCRIPTIONS_PAGE_SIZE + 1;
  const pageEnd = Math.min(
    pageStart + orderedSubscriptions.length - 1,
    totalItems
  );

  return (
    <div className="h-full flex flex-col">
      <div className="themed-scrollbar flex-1 overflow-auto min-h-0 relative">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table removeOverflowX={true} className="text-xs">
            <TableHeader className="sticky top-0 z-10 text-muted-foreground shadow-sm">
              <TableRow>
                {isEditMode && <TableHead className="w-8 h-10" />}
                <TableHead className="pl-3 h-10">Type</TableHead>
                <TableHead className="h-10 w-full max-w-0 min-w-[100px] overflow-hidden">
                  Navn
                </TableHead>
                <TableHead className="w-[60px] text-center h-10">Status</TableHead>
              </TableRow>
            </TableHeader>
            <Droppable droppableId="subscriptions-items">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {orderedSubscriptions.map((sub, index) => (
                    <Draggable
                      key={sub.id}
                      draggableId={sub.id}
                      index={index}
                      isDragDisabled={!isEditMode}
                    >
                      {(draggableProvided, snapshot) => {
                        const {
                          style: dragStyle,
                          ...draggableRowProps
                        } = draggableProvided.draggableProps;
                        return (
                        <TableRow
                          ref={draggableProvided.innerRef}
                          {...draggableRowProps}
                          style={
                            dragStyle as React.ComponentProps<
                              typeof TableRow
                            >["style"]
                          }
                          className={`h-10 ${
                            !isEditMode ? "cursor-pointer" : ""
                          } ${snapshot.isDragging ? "bg-muted/40" : ""}`}
                          title={
                            !isEditMode &&
                            sub.entityType !== SubscriptionCategory.Conversation
                              ? NEW_TAB_MODIFIER_TOOLTIP
                              : undefined
                          }
                          onClick={(e) =>
                            !isEditMode && goToSubbedItem(sub, e)
                          }
                        >
                          {isEditMode && (
                            <TableCell className="w-8 p-0">
                              <div
                                className="flex items-center justify-center cursor-grab active:cursor-grabbing"
                                {...draggableProvided.dragHandleProps}
                              >
                                <GripVertical className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </TableCell>
                          )}
                          <TableCell className="pl-3">
                            <div>{formatCategory(sub.entityType)}</div>
                          </TableCell>
                          <TableCell className="w-full max-w-0 min-w-[100px] overflow-hidden">
                            <span
                              className="truncate block"
                              title={sub.entityName ?? undefined}
                            >
                              {sub.entityName}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <SubscribeButton
                              subscribeCategory={sub.entityType}
                              entityPath={sub.entityPath}
                              source={sub.source}
                              legislationSubscriptionId={
                                sub.entityType === SubscriptionCategory.Legislation
                                  ? sub.id
                                  : undefined
                              }
                              className="hover:opacity-70 size-6"
                              strokeWidth={2}
                              isSubscribed={true}
                              entityName={sub.entityName}
                            />
                          </TableCell>
                        </TableRow>
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>

        {detailedSubscriptions.isFetching &&
          detailedSubscriptions.isPlaceholderData && (
            <div className="absolute inset-0 flex items-center justify-center bg-card/60 backdrop-blur-[1px]">
              <RefreshCw className="h-5 w-5 text-muted-foreground animate-spin" />
            </div>
          )}
      </div>

      <div className="shrink-0 flex items-center justify-between border-t border-border px-3 py-1.5 text-xs text-muted-foreground bg-card">
        <span>
          {pageStart}–{pageEnd} af {totalItems}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={currentPage <= 1 || detailedSubscriptions.isFetching}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="tabular-nums px-1">
            {currentPage} / {totalPages || 1}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={
              currentPage >= totalPages || detailedSubscriptions.isFetching
            }
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsWidget;
